import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { dismissWorkflowBomUpdate, getBomLibraryDetail, getWorkflowBomLink } from '../bomLibrary/bomLibraryApi'
import { resolveApprovedUpdate } from './bomApprovedUpdate'
import BomUpdateBanner from './BomUpdateBanner'
import BomUpdateImpactDialog from './BomUpdateImpactDialog'
import LinkBomDialog from './LinkBomDialog'
import PublishBomDialog from './PublishBomDialog'
import { SmartDialog } from '../../generic/utilities/SmartDialog'

/**
 * WorkflowBomLinkHeader — S73/S92/S111/S122/S148 (HU-05/HU-06/HU-07): header of the workflow's BOM
 * section. It is the only place that tells the user WHERE the components come from, and the place
 * every library operation on this workflow is started from.
 *
 * It reads the link itself (N15) because it is the component that renders it, and lifts it to the
 * editor through `onLinkChange`: the editor needs it to gate the import (S90), to decide the
 * post-import dialog (S113) and to warn before the save that creates version n+1 (S86).
 *
 * S92 (regression zero): N15 requires `BOM_LIBRARY|BOM_LIBRARY_EDIT`, so a user with neither
 * permission must NOT be asked for it (it would be a 403); such a user sees the plain "Embedded BOM"
 * caption of a workflow that owns its BOM and nothing of the library at all.
 *
 * @param {object} props
 * @param {number} props.workflowId the saved workflow's id; 0 for a workflow never saved (no link yet)
 * @param {string} props.tempWorkflowUUID the editor's temp scope, needed by the publish endpoints
 * @param {boolean} props.editing whether the workflow is in edit mode
 * @param {boolean} props.canReadLibrary `BOM_LIBRARY` or `BOM_LIBRARY_EDIT`
 * @param {boolean} props.canEditLibrary `BOM_LIBRARY_EDIT`
 * @param {boolean} props.hasWorkflowResources whether the workflow has a BOM at all
 * @param {*} props.refreshKey bump it to re-read the link (after a save, an import, a revert)
 * @param {object} props.pendingBomLink S71 (corrección PO 2026-09-09): the library BOM version the
 * user chose in this editing session, not written yet — `{mode, bomId, code, name, versionNumber,
 * indexValue, confirmDiscardEmbedded}`, `{}` when there is nothing pending. It is the BOM origin
 * shown while it lasts, because it is the structure the editor is already displaying. `mode` is
 * `'LINK'` (S71, N16) or `'UPDATE'` (S135, N20, corrección PO 2026-09-10).
 * @param {function(object)} props.onBomLinkSelected S71: called with the chosen version plus
 * `confirmDiscardEmbedded` so the editor records it as a pending change of the workflow. The link
 * itself (N16) is written by the general save, never from here.
 * @param {function(object)} props.onBomUpdateSelected S135 (corrección PO 2026-09-10): the same for
 * a move to a newer version of the ALREADY linked BOM. It is a separate entry point because the
 * editor has to re-map the assignments onto the new structure before recording the choice; when it
 * is not provided the choice goes straight to `onBomLinkSelected`.
 * @param {function(object)} props.onLinkChange called with the link every time it is (re)read
 * @param {function()} props.onStructureChange called when an operation changed the structure the
 * editor is showing, so it reloads it
 */
const WorkflowBomLinkHeader = ({
  workflowId = 0,
  tempWorkflowUUID = '',
  editing = false,
  canReadLibrary = false,
  canEditLibrary = false,
  hasWorkflowResources = false,
  refreshKey = '',
  pendingBomLink = {},
  onBomLinkSelected = () => {},
  onBomUpdateSelected = onBomLinkSelected,
  onLinkChange = () => {},
  onStructureChange = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [link, setLink] = useState({})
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  // S71 (RN-05.2): the selection waiting for the user to confirm what it will discard.
  const [selectionToConfirm, setSelectionToConfirm] = useState({})
  const [confirmationMessage, setConfirmationMessage] = useState('')
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  // Corrección PO 2026-09-10: the version actually on offer, `{}` when there is none. See the
  // effect below for why it is not simply `link.latestBomId`.
  const [approvedUpdate, setApprovedUpdate] = useState({})
  const [impactDialogOpen, setImpactDialogOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const readLink = useCallback(async () => {
    if (!workflowId || !canReadLibrary) {
      return
    }
    const result = await getWorkflowBomLink(workflowId)
    setLink(result || {})
    onLinkChange(result || {})
    // `onLinkChange` is deliberately out of the dependency list: the editor passes a fresh closure
    // on every render, and depending on it would re-read the link in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, canReadLibrary])

  useEffect(() => {
    readLink()
  }, [readLink, refreshKey])

  // S71 (corrección PO 2026-09-09): while a choice is pending, THAT is the BOM the editor shows.
  const hasPendingLink = Boolean(pendingBomLink?.bomId)

  // Corrección PO 2026-09-10 (S122): only an APPROVED version newer than the linked one is worth
  // announcing. Sun resolves `updateAvailable`/`latestBomId` WITHOUT looking at the index
  // (contrato-sun.md §7/D19) and the link carries no index for that latest version, so the index is
  // read here from the BOM detail (N2) and the rule applied by `resolveApprovedUpdate`.
  useEffect(() => {
    let current = true
    // With a choice pending the notice is not shown at all (S71): there is nothing to resolve.
    if (!link.linked || !link.code || hasPendingLink) {
      setApprovedUpdate({})
      return () => { current = false }
    }
    // Sun saying there is nothing newer at all is enough to stop: N2 could only confirm it. A
    // candidate resolved earlier is KEPT for the same reason S148 exists — after a dismissal the
    // header must still say the workflow stayed on an older version than the approved one.
    if (!link.updateAvailable) {
      setApprovedUpdate((previous) => (previous.code === link.code ? previous : {}))
      return () => { current = false }
    }
    getBomLibraryDetail(link.code)
      .then((detail) => {
        if (!current) return
        setApprovedUpdate({ ...resolveApprovedUpdate(link, detail?.versions || []), code: link.code })
      })
      // A detail that cannot be read is not a reason to break the header: without it there is
      // simply no version to offer.
      .catch(() => { if (current) setApprovedUpdate({ code: link.code }) })
    return () => { current = false }
  }, [link, hasPendingLink])

  // S92 (RN-05.9): the workflow owns its BOM. Without either library permission the link was never
  // asked for, so the presence of a BOM is all Jupiter knows — and it is enough, because a user with
  // no library permission can only ever be looking at an embedded one.
  const showsEmbedded = link.embedded === true || (!canReadLibrary && hasWorkflowResources)

  // S71 (RN-05.2): linking is never silent about what it throws away. Sun's own two literals
  // (contrato-sun.md §4, E-LNK-01/E-LNK-02) are the message, and the confirmation now happens
  // BEFORE the choice is recorded — the request that would have been rejected runs at save time.
  const discardMessageFor = () => {
    if (link.linked === true) {
      return 'Workflow is already linked to a BOM version'
    }
    if (link.embedded === true || hasWorkflowResources) {
      return 'Workflow has an embedded BOM; linking will discard it'
    }
    return ''
  }

  const onSelected = (selection) => {
    setLinkDialogOpen(false)
    const message = discardMessageFor()
    if (message) {
      setSelectionToConfirm(selection)
      setConfirmationMessage(message)
      return
    }
    onBomLinkSelected({ ...selection, confirmDiscardEmbedded: false })
  }

  const confirmSelection = () => {
    onBomLinkSelected({ ...selectionToConfirm, confirmDiscardEmbedded: true })
    setSelectionToConfirm({})
    setConfirmationMessage('')
  }

  // S101/S102: what was an embedded BOM is now a library BOM AND this workflow is linked to it, so
  // the caption, the banner and the components all have to be read again.
  const onPublished = async () => {
    setPublishDialogOpen(false)
    onStructureChange()
    await readLink()
  }

  // S148 (RN-07.7): the dismissal is recorded against the version that was being offered — the
  // highest APPROVED one — so the notice comes back only when a version newer than that appears.
  // Nothing else changes: the link, the version in use and the structure stay as they were.
  const dismissUpdate = async () => {
    setBusy(true)
    const result = await dismissWorkflowBomUpdate(workflowId, approvedUpdate.versionNumber)
    if (result) {
      setLink(result)
      onLinkChange(result)
    }
    setBusy(false)
  }

  // S135: the update targets the version the banner announced (the highest APPROVED one), which is
  // also newer than the linked one, as Sun requires (E-LNK-05 otherwise).
  //
  // Corrección PO 2026-09-10: accepting the impact writes NOTHING. It becomes a pending change of
  // the workflow, exactly like choosing a BOM to link (S71) — the difference travels in `mode`, and
  // it is the general save that turns it into N20 instead of N16. So "Revert" undoes it.
  const onUpdateSelected = () => {
    setImpactDialogOpen(false)
    onBomUpdateSelected({
      mode: 'UPDATE',
      bomId: approvedUpdate.bomId,
      code: link.code,
      name: link.name,
      versionNumber: approvedUpdate.versionNumber,
      indexValue: approvedUpdate.indexValue,
      confirmDiscardEmbedded: false
    })
  }

  // S148: 'latest' names the highest APPROVED version, the same one the banner offered — a draft
  // above it is not a version this workflow could have been on.
  const usesOlderThanLatest = Boolean(link.linked) && Number(approvedUpdate.versionNumber || 0) > Number(link.versionNumber)

  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
        {/* S71 (corrección PO 2026-09-09): the pending choice IS the BOM origin on screen — the
            components already come from that version — and it is flagged as not written yet, so the
            user knows the general save is what applies it. */}
        {hasPendingLink
          ? (
            <Stack direction='row' spacing={1} alignItems='center' data-testid='bom-link-header'>
              <Chip size='small' color='primary' variant='outlined' label={t_('Library BOM')} />
              <Typography variant='subtitle2'>{pendingBomLink.code}</Typography>
              <Typography variant='body2'>{pendingBomLink.name}</Typography>
              <Typography variant='body2' data-testid='bom-link-version'>
                {t_('Version')} {pendingBomLink.versionNumber}
                {/* S135: a pending UPDATE moves the workflow from the version it is linked to today
                    to the chosen one; saying only the target would hide what is being left behind. */}
                {pendingBomLink.mode === 'UPDATE' && link.linked
                  ? ' (' + t_('from {{version}}', { version: link.versionNumber }) + ')'
                  : ''}
              </Typography>
              {pendingBomLink.indexValue ? <Chip size='small' label={t_(pendingBomLink.indexValue)} /> : null}
              <Chip size='small' color='warning' label={t_('Pending save')} data-testid='bom-link-pending-save' />
            </Stack>
            )
          : null}
        {!hasPendingLink && link.linked
          ? (
            <Stack direction='row' spacing={1} alignItems='center' data-testid='bom-link-header'>
              <Chip size='small' color='primary' variant='outlined' label={t_('Library BOM')} />
              <Typography variant='subtitle2'>{link.code}</Typography>
              <Typography variant='body2'>{link.name}</Typography>
              <Typography variant='body2' data-testid='bom-link-version'>
                {t_('Version')} {link.versionNumber}
                {/* S148: after dismissing the notice the workflow keeps using an older version. The
                    banner is gone, but the header must still say so — silently pointing at version 2
                    while version 3 exists is exactly what the user chose, not something to hide. */}
                {usesOlderThanLatest ? ' (' + t_('latest: {{version}}', { version: approvedUpdate.versionNumber }) + ')' : ''}
              </Typography>
              {link.indexValue ? <Chip size='small' label={t_(link.indexValue)} /> : null}
            </Stack>
            )
          : null}
        {!hasPendingLink && !link.linked && showsEmbedded
          ? (
            <Stack direction='row' spacing={1} alignItems='center' data-testid='bom-link-header'>
              <Chip size='small' variant='outlined' label={t_('Embedded BOM')} />
            </Stack>
            )
          : null}
        {/* S71: linking is an edit of the workflow and needs `BOM_LIBRARY`; a workflow that was
            never saved has no id to link (the link is a row of its own, keyed by workflow). */}
        {editing && canReadLibrary
          ? (
            <Button size='small' variant='outlined' onClick={() => setLinkDialogOpen(true)} data-testid='bom-link-open-link-dialog'>
              {t_('Link library BOM')}
            </Button>
            )
          : null}
        {/* S111 (RN-06.7/CA6.5): publishing needs `BOM_LIBRARY_EDIT`, and only an EMBEDDED BOM can
            be published — a linked one already is a library BOM (E-LNK-06). Publishing the result of
            an import over a LINKED workflow is a different decision, taken right after that import
            (S113, `PublishAfterImportDialog`). */}
        {editing && canEditLibrary && workflowId && hasWorkflowResources && !link.linked && !hasPendingLink
          ? (
            <Button size='small' variant='outlined' onClick={() => setPublishDialogOpen(true)} data-testid='bom-link-open-publish-dialog'>
              {t_('Publish to library')}
            </Button>
            )
          : null}
      </Stack>
      {/* S71: with a choice pending, the CURRENT link's update notice would point at a BOM the
          workflow is about to stop using — it comes back after the save, if it still applies. */}
      {hasPendingLink
        ? null
        : <BomUpdateBanner link={link} update={approvedUpdate} onUpdate={() => setImpactDialogOpen(true)} onDismiss={dismissUpdate} busy={busy} />}
      {/* The dialogs are mounted only while open, so every opening starts from a clean state
          (no stale search, preview or impact from a previous use). */}
      {impactDialogOpen
        ? (
          <BomUpdateImpactDialog
            open
            workflowId={workflowId}
            targetBomId={approvedUpdate.bomId || 0}
            onUpdateSelected={onUpdateSelected}
            onCancel={() => setImpactDialogOpen(false)}
          />
          )
        : null}
      {linkDialogOpen
        ? (
          <LinkBomDialog
            open
            onSelected={onSelected}
            onCancel={() => setLinkDialogOpen(false)}
          />
          )
        : null}
      {/* S71 (RN-05.2): the discard is confirmed with Sun's own literal BEFORE the choice becomes a
          pending change; "Continue" records it with `confirmDiscardEmbedded`, which is what the save
          will send to N16. */}
      <SmartDialog
        setOpen={Boolean(confirmationMessage)}
        title='default'
        message={t_(confirmationMessage)}
        acceptLabel='Continue'
        acceptCallback={confirmSelection}
        cancelCallback={() => { setSelectionToConfirm({}); setConfirmationMessage('') }}
        autoClose={false}
      />
      {publishDialogOpen
        ? (
          <PublishBomDialog
            open
            workflowId={workflowId}
            tempWorkflowUUID={tempWorkflowUUID}
            hasWorkflowResources={hasWorkflowResources}
            onPublished={onPublished}
            onCancel={() => setPublishDialogOpen(false)}
          />
          )
        : null}
    </Stack>
  )
}

export default WorkflowBomLinkHeader
